import { enqueueSnackbar } from "notistack";
import { isAuthenticated } from "../utils/auth";
import { isOlivsApiSuccess } from "../utils/api";
import {
  getAuthxAppsErrorMessage,
  isAuthxAppsExplicitFailureBody,
  isAuthxAppsFlowHandledBody,
  isAuthxAppsRequestUrl,
  isAuthxAppsSuccessBody,
} from "../utils/authx-apps-response";
import {
  dispatchSessionRefreshed,
  refreshAuthxSessionCoalesced,
} from "../utils/authx-session-refresh";
import {
  responseDataIndicatesSessionInvalid,
  shouldSuppressErrorSnackbarForHttpStatus,
} from "../utils/session-reauth";
import { mergeOlivsAuthIntoHeaders } from "../utils/olivs-auth-headers";

/**
 * Global API Interceptor
 *
 * Intercepts ALL fetch calls to check for SuccessYN=N and displays errors
 * No exceptions, no patterns to match - just check everything
 */

// Save the original fetch
const originalFetch = window.fetch;

// Store the login opener function for later use
let loginOpener = null;

// Store error modal opener function for new error format
let errorModalOpener = null;

// Flag to indicate if authentication is in progress
let isAuthenticationInProgress = false;

// Pending requests queue (requests that were attempted during authentication)
const pendingRequests = [];

// Function to check if we're in SessionManagement flow
const isSessionManagementFlow = () => {
  return (
    window.location.pathname.includes("/session-management/") ||
    window.location.pathname.includes("/SessionManagement/") ||
    window.location.pathname.includes("/authorization/validation/")
  );
};

const isMfaVerificationFlow = () => window.location.pathname === "/mfa";

// Function to set the login opener from outside (will be called by LoginPopupContext)
export const setLoginOpener = (opener) => {
  console.log("🔐 API Interceptor: Login opener registered");
  loginOpener = opener;
};

// Function to set the error modal opener from outside
export const setErrorModalOpener = (opener) => {
  console.log("🔐 API Interceptor: Error modal opener registered");
  errorModalOpener = opener;
};

// Function to handle new error format (ErMsg structure)
const handleNewErrorFormat = (errorData, logId) => {
  if (!errorData.ErMsg || typeof errorData.ErMsg !== "object") {
    return false; // Not new format
  }

  const erMsg = errorData.ErMsg;
  const displayAs = erMsg.DisplayAs; // 'T' for Toast, 'M' for Modal
  const msgType = erMsg.MsgType; // 'E', 'A', 'I', 'R'
  const header = erMsg.Header;
  const text = erMsg.Text;
  const hideLogId = erMsg.HideLogIdYN === "Y";

  // Add log ID to message if available and not hidden
  let finalText = text;
  if (logId && !hideLogId) {
    finalText += ` Log ID: ${logId}`;
  }

  if (displayAs === "M" && errorModalOpener) {
    // Show as modal - pass the entire ErMsg structure plus the log ID
    errorModalOpener({
      ...erMsg,
      Text: finalText,
      LogId: logId, // Pass log ID separately so modal can use it
    });
    return true;
  } else {
    // Show as toast (default fallback)
    const variant =
      msgType === "E"
        ? "error"
        : msgType === "A"
        ? "warning"
        : msgType === "I"
        ? "info"
        : "success";

    const message = header ? `${header}: ${finalText}` : finalText;
    enqueueSnackbar(message, { variant });
    return true;
  }
};

// Function to open login popup if needed - force immediate opening
const openLoginIfNeeded = () => {
  // Skip during SessionManagement flow to prevent popup flash
  if (isSessionManagementFlow()) {
    console.log("🔐 API Interceptor: Skipping - SessionManagement flow");
    return false;
  }

  if (isMfaVerificationFlow()) {
    console.log("🔐 API Interceptor: Skipping - MFA verification flow");
    return false;
  }

  if (!isAuthenticated()) {
    console.log(
      "🔐 API Interceptor: Not authenticated, attempting to open popup"
    );

    // If authentication is already in progress, don't open another popup
    if (isAuthenticationInProgress) {
      console.log("🔐 API Interceptor: Auth in progress, skipping duplicate");
      return true;
    }

    if (loginOpener) {
      console.log("🔐 API Interceptor: Opening login popup");
      try {
        // Set flag before calling opener
        isAuthenticationInProgress = true;

        // Call the opener directly with 'login' type
        loginOpener("login").catch(() => {
          // Reset flag if login fails
          isAuthenticationInProgress = false;

          // Process any pending requests with failure
          processPendingRequests(false);
        });
        return true;
      } catch (e) {
        console.error("🔐 API Interceptor: Error calling login opener", e);
        isAuthenticationInProgress = false;
      }
    } else {
      console.log("🔐 API Interceptor: Login opener not registered");
    }
  }
  return false;
};

// Function to be called when login completes/cancels
export const notifyLoginComplete = (success = false) => {
  console.log("🔐 API Interceptor [FETCH]: Login notification received", {
    success,
    pendingCount: pendingRequests.length,
    wasAuthInProgress: isAuthenticationInProgress,
    timestamp: new Date().toISOString(),
  });
  isAuthenticationInProgress = false;

  // If authentication was successful, process any pending requests
  processPendingRequests(success);
};

// Process pending requests after authentication completes
const processPendingRequests = (authSuccess) => {
  console.log(`🔐 API Interceptor [FETCH]: Processing pending requests`, {
    authSuccess,
    pendingCount: pendingRequests.length,
    timestamp: new Date().toISOString(),
  });

  if (authSuccess) {
    // If auth was successful, retry the requests
    let processed = 0;
    while (pendingRequests.length > 0) {
      const request = pendingRequests.shift();
      if (request) {
        processed++;
        console.log(
          `🔐 API Interceptor [FETCH]: Retrying request ${processed}`,
          {
            url: request.args[0]?.toString().split("?")[0] || "unknown",
          }
        );
        originalFetch
          .apply(window, request.args)
          .then(request.resolve)
          .catch(request.reject);
      }
    }
    console.log(
      `🔐 API Interceptor [FETCH]: Finished processing ${processed} requests`
    );
  } else {
    // If auth failed, reject all pending requests
    let rejected = 0;
    while (pendingRequests.length > 0) {
      const request = pendingRequests.shift();
      if (request) {
        rejected++;
        request.reject(new Error("Authentication failed"));
      }
    }
    console.log(
      `🔐 API Interceptor [FETCH]: Rejected ${rejected} pending requests`
    );
  }
};

// Track if we've already triggered the login popup for this page load
let hasTriggeredLoginPopup = false;

// Referrer only — cookies alone must not block 401 → refresh-key recovery
const cameFromSessionManagement = () => {
  const referrer = document.referrer;
  return (
    referrer.includes("/session-management/") ||
    referrer.includes("/SessionManagement/") ||
    referrer.includes("/authorization/validation/")
  );
};

const OLIVS_FETCH_RETRY_AFTER_REFRESH = "x-olivs-retry-after-refresh";

function isIgnoredThirdPartyRequest(url) {
  return url.toLowerCase().includes("tawk.to");
}

function fetchArgsIncludeRetryAfterRefresh(args) {
  const init = args[1];
  if (!init?.headers) return false;
  const h = init.headers;
  if (typeof h.get === "function") {
    return h.get(OLIVS_FETCH_RETRY_AFTER_REFRESH) === "true";
  }
  if (Array.isArray(h)) {
    return h.some(
      ([k, v]) => k === OLIVS_FETCH_RETRY_AFTER_REFRESH && v === "true",
    );
  }
  return h[OLIVS_FETCH_RETRY_AFTER_REFRESH] === "true";
}

function headersInitToRecord(headers) {
  if (!headers) return {};
  if (typeof headers.forEach === "function") {
    const out = {};
    headers.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...headers };
}

function rebuildFetchArgsWithFreshAuth(args) {
  const [input, init] = args;
  const base = init ?? {};
  const merged = mergeOlivsAuthIntoHeaders(
    headersInitToRecord(base.headers),
    "0",
  );
  const headers = new Headers(merged);
  headers.set(OLIVS_FETCH_RETRY_AFTER_REFRESH, "true");
  return [input, { ...base, headers }];
}

const promptLoginAfterSessionLoss = () => {
  if (
    isSessionManagementFlow() ||
    isMfaVerificationFlow() ||
    isAuthenticationInProgress
  ) {
    return;
  }
  if (loginOpener) {
    isAuthenticationInProgress = true;
    loginOpener("login").catch(() => {
      isAuthenticationInProgress = false;
      processPendingRequests(false);
    });
    hasTriggeredLoginPopup = true;
  }
};

// Immediately check authentication on script load
setTimeout(() => {
  if (
    !isAuthenticated() &&
    !hasTriggeredLoginPopup &&
    !isSessionManagementFlow() &&
    !isMfaVerificationFlow() &&
    !cameFromSessionManagement()
  ) {
    console.log("🔐 API Interceptor: Initial auth check - opening popup");
    hasTriggeredLoginPopup = openLoginIfNeeded();
  }
}, 500);

// Function to check if a URL is an API call
const isApiRequest = (url) => {
  if (isIgnoredThirdPartyRequest(url)) {
    return false;
  }
  return (
    url.includes("/api/") ||
    url.includes("apiDev1") ||
    url.includes("apiDev2") ||
    url.includes("authx-api.") ||
    url.includes("BaseHostURL=")
  );
};

const isLoginRelatedEndpoint = (url) =>
  url.includes("/generate-new-api-token-details") ||
  url.includes("/validate-2fa-code") ||
  url.includes("/api/apps/") ||
  url.includes("/login") ||
  url.includes("/users/create-new");

const appendLogId = (message, logId) =>
  logId && message ? `${message} Log ID: ${logId}` : message;

const getRequestHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") {
    return headers.get(name) ?? headers.get(name.toLowerCase());
  }
  return headers[name] ?? headers[name.toLowerCase()];
};

/**
 * Plain text or JSON body → snackbar.
 * Handles flat string errors (AuthX verify-mfa), JSON string literals,
 * SuccessYN=N, and AuthX explicit failures on any HTTP status.
 */
const showErrorFromResponseBody = (text, response, url = "") => {
  if (typeof text !== "string" || !text.trim()) {
    return false;
  }

  const logId = response.headers.get("logid");
  const trimmed = text.trim();
  const httpOk = response.ok;

  if (isAuthxAppsFlowHandledBody(trimmed)) {
    return false;
  }

  try {
    const jsonData = JSON.parse(trimmed);

    if (typeof jsonData === "string") {
      if (!jsonData.trim() || isAuthxAppsFlowHandledBody(jsonData)) {
        return false;
      }
      if (!shouldSuppressErrorSnackbarForHttpStatus(response.status)) {
        enqueueSnackbar(appendLogId(jsonData.trim(), logId), { variant: "error" });
      }
      return true;
    }

    if (!jsonData || typeof jsonData !== "object") {
      return false;
    }

    if (isAuthxAppsFlowHandledBody(jsonData)) {
      return false;
    }

    if (isOlivsApiSuccess(jsonData, httpOk)) {
      return false;
    }
    if (httpOk && isAuthxAppsSuccessBody(jsonData)) {
      return false;
    }

    if (jsonData.SuccessYN === "N" || jsonData.successYN === "N") {
      if (handleNewErrorFormat(jsonData, logId)) {
        return true;
      }
    } else if (jsonData.ErMsg && typeof jsonData.ErMsg === "object") {
      if (handleNewErrorFormat(jsonData, logId)) {
        return true;
      }
    }

    const errorMessage =
      getAuthxAppsErrorMessage(jsonData) ||
      (jsonData.error != null ? String(jsonData.error).trim() : "");

    if (errorMessage) {
      if (!shouldSuppressErrorSnackbarForHttpStatus(response.status)) {
        enqueueSnackbar(appendLogId(errorMessage, logId), { variant: "error" });
      }
      return true;
    }

    if (
      httpOk &&
      isAuthxAppsRequestUrl(url) &&
      isAuthxAppsExplicitFailureBody(jsonData)
    ) {
      return false;
    }

    return false;
  } catch {
    if (!shouldSuppressErrorSnackbarForHttpStatus(response.status)) {
      enqueueSnackbar(appendLogId(trimmed, logId), { variant: "error" });
    }
    return true;
  }
};

const maybeOpenLoginPopupForUnauthorized = (response, url, skipAuthInterceptor) => {
  if (
    skipAuthInterceptor ||
    !isApiRequest(url) ||
    response.status !== 403
  ) {
    return response;
  }

  const isLoginEndpoint = isLoginRelatedEndpoint(url);

  if (
    !isAuthenticationInProgress &&
    !isSessionManagementFlow() &&
    !isMfaVerificationFlow() &&
    !isLoginEndpoint
  ) {
    promptLoginAfterSessionLoss();
  }

  return response;
};

const maybeOpenLoginPopupForSuccessYnFailure = (
  text,
  url,
  skipAuthInterceptor,
) => {
  if (skipAuthInterceptor || !isApiRequest(url)) {
    return;
  }

  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    return;
  }

  if (
    !data ||
    typeof data !== "object" ||
    (data.SuccessYN !== "N" && data.successYN !== "N")
  ) {
    return;
  }

  const isLoginEndpoint = isLoginRelatedEndpoint(url);
  if (
    !isAuthenticationInProgress &&
    !isSessionManagementFlow() &&
    !isLoginEndpoint &&
    !isAuthenticated()
  ) {
    hasTriggeredLoginPopup = openLoginIfNeeded();
  }
};

const processApiResponseForErrors = (response, url, skipAuthInterceptor) => {
  if (!isApiRequest(url)) {
    return Promise.resolve(response);
  }

  return response
    .clone()
    .text()
    .then((text) => {
      if (responseDataIndicatesSessionInvalid(text)) {
        promptLoginAfterSessionLoss();
      }
      showErrorFromResponseBody(text, response, url);
      maybeOpenLoginPopupForSuccessYnFailure(text, url, skipAuthInterceptor);
      return maybeOpenLoginPopupForUnauthorized(
        response,
        url,
        skipAuthInterceptor,
      );
    })
    .catch((err) => {
      console.log("🔐 API Interceptor: Error reading response body", err);
      return maybeOpenLoginPopupForUnauthorized(
        response,
        url,
        skipAuthInterceptor,
      );
    });
};

// Monkey-patch fetch to intercept all API calls
window.fetch = function (...args) {
  // Check if this is an API call (not static assets, etc.)
  const url = args[0]?.toString() || "";

  // Check if request should skip auth popup / queue only (errors still handled)
  const requestInit = args[1];
  const headers = requestInit?.headers;
  const skipAuthInterceptor =
    getRequestHeader(headers, "x-skip-auth-interceptor") === "true";

  if (skipAuthInterceptor || isIgnoredThirdPartyRequest(url)) {
    return originalFetch.apply(this, args);
  }

  // Only check auth for API calls
  const isApiCall = isApiRequest(url);

  // Only check auth for API calls, but skip during SessionManagement flow or if we came from it
  if (
    isApiCall &&
    !skipAuthInterceptor &&
    !hasTriggeredLoginPopup &&
    !isSessionManagementFlow() &&
    !isMfaVerificationFlow() &&
    !cameFromSessionManagement()
  ) {
    // If authentication is in progress, queue the request instead of executing it
    if (isAuthenticationInProgress) {
      return new Promise((resolve, reject) => {
        // Store this request in the pending queue
        pendingRequests.push({ resolve, reject, args });
      });
    }

    // Force immediate auth check before proceeding
    hasTriggeredLoginPopup = openLoginIfNeeded();
    if (hasTriggeredLoginPopup) {
      return new Promise((resolve, reject) => {
        // Store this request in the pending queue
        pendingRequests.push({ resolve, reject, args });
      });
    }
  }

  try {
    // Call the original fetch
    console.log(`🔐 API Interceptor [FETCH]: About to call original fetch`, {
      url: url.split("?")[0],
      isApiCall: isApiCall,
    });
    const promiseResponse = originalFetch.apply(this, args);

    // Add response handler as a promise chain
    return promiseResponse.then(async (response) => {
      console.log(`🔐 API Interceptor [FETCH]: Response received`, {
        url: url.split("?")[0],
        status: response.status,
        isApiCall: isApiCall,
      });

      if (
        response.status === 401 &&
        isApiCall &&
        process.env.NODE_ENV !== "development" &&
        !fetchArgsIncludeRetryAfterRefresh(args) &&
        !isLoginRelatedEndpoint(url)
      ) {
        const refreshed = await refreshAuthxSessionCoalesced();
        if (refreshed) {
          dispatchSessionRefreshed();
          const retryResponse = await window.fetch.apply(
            this,
            rebuildFetchArgsWithFreshAuth(args),
          );
          return processApiResponseForErrors(
            retryResponse,
            url,
            skipAuthInterceptor,
          );
        }
        promptLoginAfterSessionLoss();
        return response;
      }

      return processApiResponseForErrors(
        response,
        url,
        skipAuthInterceptor,
      );
    });
  } catch (error) {
    console.log("🔐 API Interceptor: Caught error", {
      error,
      message: error.message,
      type: error.constructor.name,
      isApiCall,
      stack: error.stack?.substring(0, 200),
    });

    // ONLY show "Lost connection" for TRUE network failures
    // Check for very specific network error conditions
    const isRealNetworkError =
      error.name === "TypeError" && error.message === "Failed to fetch";

    if (isApiCall && !isAuthenticationInProgress && isRealNetworkError) {
      if (isAuthenticated()) {
        enqueueSnackbar(
          `Lost connection to Exacc server. Please check your internet connection and try again.`,
          {
            variant: "error",
          }
        );
      } else if (!isSessionManagementFlow()) {
        hasTriggeredLoginPopup = openLoginIfNeeded();
      }
    }

    // Always re-throw the error so the calling code can handle it
    throw error;
  }
};

console.log("🔐 API Interceptor: Initialized");
